import React, { useState, useEffect } from 'react';
import { apiKeyService } from '../../services/apiKeyService';
import { apiService } from '../../services/api';

const CreateApiKey = () => {
    const [name, setName] = useState('');
    const [userId, setUserId] = useState('');
    const [permissions, setPermissions] = useState([]);
    const [expiresInDays, setExpiresInDays] = useState(365);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);
    const [createdKey, setCreatedKey] = useState(null);
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);

    // Permission options
    const permissionOptions = [
        { value: 'send-text', label: 'Send Text Messages' },
        { value: 'send-template', label: 'Send Templates' },
        { value: 'send-media', label: 'Send Media' },
        { value: 'send-buttons', label: 'Send Interactive Buttons' },
        { value: 'upload-media', label: 'Upload Media' }
    ];

    // Load users for the dropdown
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await apiService.getUsers();
                // Filter to only include users with admin role
                // API response structure is { success: true, data: { users: [...] } }
                const allUsers = response.data?.users || [];
                const adminUsers = allUsers.filter(user => user.role === 'admin' && user.isActive === true);
                setUsers(adminUsers);

                if (adminUsers.length === 0) {
                    console.log('No admin users found in response:', response);
                }
            } catch (err) {
                console.error('Failed to load users:', err);
            } finally {
                setLoadingUsers(false);
            }
        };

        fetchUsers();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!name || !userId || permissions.length === 0) {
            setError('Please fill out all required fields.');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const response = await apiKeyService.createApiKey({
                name,
                userId,
                permissions,
                expiresInDays: expiresInDays || null
            });

            setSuccess(true);
            setCreatedKey(response);

            // Reset form
            setName('');
            setUserId('');
            setPermissions([]);
            setExpiresInDays(365);
        } catch (err) {
            setError('Failed to create API key: ' + (err.message || 'Unknown error'));
            console.error('Error creating API key:', err);
        } finally {
            setLoading(false);
        }
    };

    const handlePermissionChange = (permission) => {
        if (permissions.includes(permission)) {
            setPermissions(permissions.filter(p => p !== permission));
        } else {
            setPermissions([...permissions, permission]);
        }
    };

    const handleSelectAllPermissions = () => {
        if (permissions.length === permissionOptions.length) {
            setPermissions([]);
        } else {
            setPermissions(permissionOptions.map(p => p.value));
        }
    };

    return (
        <div className="p-4">
            <h2 className="text-xl font-bold mb-4">Create New API Key</h2>

            {success && createdKey ? (
                <div className="bg-green-100 border border-green-300 text-green-800 p-4 rounded mb-4">
                    <h3 className="font-bold mb-2">API Key Created Successfully</h3>
                    <p className="mb-2">Please copy your API key now. You won't be able to see it again!</p>

                    <div className="bg-gray-100 p-3 rounded mb-3 font-mono text-sm break-all">
                        {createdKey.key}
                    </div>

                    <button
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                        onClick={() => {
                            navigator.clipboard.writeText(createdKey.key);
                            alert('API Key copied to clipboard');
                        }}
                    >
                        Copy to Clipboard
                    </button>

                    <button
                        className="ml-2 bg-gray-300 px-4 py-2 rounded hover:bg-gray-400"
                        onClick={() => {
                            setSuccess(false);
                            setCreatedKey(null);
                        }}
                    >
                        Create Another Key
                    </button>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="bg-white p-6 rounded shadow-md">
                    {error && (
                        <div className="bg-red-100 border border-red-300 text-red-600 p-3 rounded mb-4">
                            {error}
                        </div>
                    )}

                    <div className="mb-4">
                        <label className="block text-gray-700 mb-2" htmlFor="name">
                            API Key Name <span className="text-red-600">*</span>
                        </label>
                        <input
                            id="name"
                            type="text"
                            className="w-full px-3 py-2 border rounded"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="E.g., ERP Integration, CRM App"
                            required
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-gray-700 mb-2" htmlFor="userId">
                            Admin User <span className="text-red-600">*</span>
                        </label>
                        {loadingUsers ? (
                            <div className="text-gray-500">Loading admin users...</div>
                        ) : (
                            <select
                                id="userId"
                                className="w-full px-3 py-2 border rounded"
                                value={userId}
                                onChange={(e) => setUserId(e.target.value)}
                                required
                            >
                                <option value="">Select an admin user</option>
                                {users.length === 0 ? (
                                    <option value="" disabled>No admin users available</option>
                                ) : (
                                    users.map((user) => (
                                        <option key={user._id} value={user._id}>
                                            {user.username} | {user.email}
                                        </option>
                                    ))
                                )}
                            </select>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                            Only users with admin role can be associated with API keys.
                        </p>
                    </div>

                    <div className="mb-4">
                        <label className="block text-gray-700 mb-2">
                            Permissions <span className="text-red-600">*</span>
                        </label>
                        <div className="flex mb-2">
                            <button
                                type="button"
                                className="text-sm text-blue-600 hover:underline"
                                onClick={handleSelectAllPermissions}
                            >
                                {permissions.length === permissionOptions.length ? 'Deselect All' : 'Select All'}
                            </button>
                        </div>
                        {permissionOptions.map((option) => (
                            <div key={option.value} className="flex items-center mb-2">
                                <input
                                    id={`permission-${option.value}`}
                                    type="checkbox"
                                    className="mr-2"
                                    checked={permissions.includes(option.value)}
                                    onChange={() => handlePermissionChange(option.value)}
                                />
                                <label htmlFor={`permission-${option.value}`}>
                                    {option.label}
                                </label>
                            </div>
                        ))}
                    </div>

                    <div className="mb-4">
                        <label className="block text-gray-700 mb-2" htmlFor="expiresInDays">
                            Expiration
                        </label>
                        <select
                            id="expiresInDays"
                            className="w-full px-3 py-2 border rounded"
                            value={expiresInDays || ''}
                            onChange={(e) => setExpiresInDays(e.target.value ? parseInt(e.target.value) : null)}
                        >
                            <option value="">Never expires</option>
                            <option value="30">30 days</option>
                            <option value="90">90 days</option>
                            <option value="180">6 months</option>
                            <option value="365">1 year</option>
                            <option value="730">2 years</option>
                        </select>
                    </div>

                    <div className="flex justify-end">
                        <button
                            type="button"
                            className="px-4 py-2 border rounded mr-2 hover:bg-gray-100"
                            onClick={() => window.history.back()}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                            disabled={loading}
                        >
                            {loading ? 'Creating...' : 'Create API Key'}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default CreateApiKey; 